import type { CSSProperties, ReactNode } from 'react';
import { cx } from '../cx';
import s from './Marquee.module.css';

interface MarqueeProps {
  /** One pass of the track, in ms. Slow enough to read, never seen to restart. */
  duration?: number;
  reverse?: boolean;
  className?: string;
  /** The band's own hairlines, top and bottom. */
  ruled?: boolean;
  children: ReactNode;
}

/**
 * A band that runs, forever, at a constant speed.
 *
 * The track holds two identical copies and translates by exactly half its
 * width, so the seam lands on a repeat and is never visible. It is decorative
 * by definition — the same words are always available as real text elsewhere
 * on the page — so the whole thing is hidden from assistive technology and
 * frozen flat under `prefers-reduced-motion`.
 */
export function Marquee({
  duration = 28000,
  reverse = false,
  className,
  ruled = true,
  children,
}: MarqueeProps) {
  return (
    <div
      className={cx(s.band, ruled && s.ruled, className)}
      aria-hidden="true"
      style={{ '--dur': `${duration}ms` } as CSSProperties}
    >
      <div className={cx(s.track, reverse && s.reverse)}>
        <div className={s.group}>{children}</div>
        <div className={s.group}>{children}</div>
      </div>
    </div>
  );
}
